import React, { memo, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWtrSelector } from 'redux/hooks';
import classNames from 'classnames';

import Alert from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton/LinkAsButton';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { Snackbar } from '@johnlewispartnership/wtr-ingredients/ingredients/Snackbar';
import { PageTitle } from '@johnlewispartnership/wtr-ingredients/ingredients/PageTitle';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { Help, CircleAdd } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { colourScallopGrey } from '@johnlewispartnership/wtr-ingredients/foundations/colours';
import ExperienceFragment from 'components/ExperienceFragment';

import type { DateString } from 'utils/date-string';
import { GA_MEAL_DEAL_RECOMMENDED_CAROUSEL_TYPE } from 'utils/clickContext/recommendedCarouselContext';
import urls from 'constants/urls';
import { BREAKPOINTS } from 'constants/grid';

import { getPageLoading } from 'redux/modules/page/selectors';
import {
  getMealDealBuildersLength,
  getMealDealProductsGrouped,
  getMealDealNotFound,
  getMealDealLoading,
  getMealDealUnrecoverableError,
  getMealDealRecommendedProducts,
  getMealDealErrorCode,
  getMealDealStartDate,
  getMealDealEndDate,
  getMealDealMetadata,
  getMealDealTitle,
} from 'redux/modules/meal-deals/selectors';
import { getCustomerSlotDate } from 'redux/modules/customer-slot/selectors';
import { getEditSlotUrl } from 'redux/modules/slot-booking/selectors/get-edit-slot-url';
import { hasHeaderContent } from 'redux/modules/content/selectors';

import NotFound from 'components/NotFound/NotFound';
import Breadcrumbs from 'components/Breadcrumbs';
import MetaHeader from 'components/MetaHeader';
import ProductGrid from 'components/ProductGrid/ProductGrid';
import RecommendedCarousel from 'components/RecommendedCarousel';
import ContentLocation from 'components/ContentLocation';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import Scene7Image from 'components/Image/Scene7Image';
import Skeleton from 'components/Skeleton';
import Builder, { CountAndTotal } from 'components/Builder';
import { canShowRecommended } from 'components/RecommendedCarousel/canShowRecommended';
import { getMealDealProgressMessage } from 'components/MealDealPage/get-meal-deal-progress-message';
import { getMealDealDateDrivenConfig } from 'components/MealDealPage/get-meal-deal-date-driven-config';
import {
  ENDING_SOON_CAUSE,
  INVALID_SLOT_CAUSE,
  INVALID_BRANCH_CAUSE,
  SEARCH_TYPE,
} from 'components/MealDealPage/constants';
import { ListAnalytics } from 'analytics/types';

import styles from './MealDealPage.scss';

const EMPTY_PRODUCTS: never[] = [];

const TAXONOMY_LEVEL = 'F0';
const LOCATION = 'meal deal';

const BUILDER_INITIAL_MESSAGE = 'Create your meal deal';
const BUILDER_SUBSEQUENT_MESSAGE = 'Create another meal deal';
const BUILDER_COMPLETE_MESSAGE = 'Your meal deal is complete';
const BUILDERS_WARNING_MESSAGE =
  'When two or more items from the same category are added, the cheapest will be included in the meal deal';

const RECOMMENDATIONS_TITLE = 'The perfect match';
const RECOMMENDATIONS_SUB_TITLE = 'Please note these are not included in the deal.';

const GroupItemPlaceholder = memo(() => (
  <div className={styles.placeholderWrapper}>
    <Help size="large" className={styles.helpIcon} />
    <CircleAdd size="large" className={styles.circleAddIcon} />
  </div>
));

GroupItemPlaceholder.displayName = 'GroupItemPlaceholder';

const getAlertAction = (alertCause: symbol | undefined, editSlotUrl: string) => {
  switch (alertCause) {
    case INVALID_SLOT_CAUSE: {
      return (
        <TextLink component={Link} to={editSlotUrl} data-testid="change-slot-link">
          Change slot
        </TextLink>
      );
    }
    case ENDING_SOON_CAUSE: {
      return (
        <TextLink component={Link} to={urls.serviceSelection} data-testid="book-slot-link">
          Book a slot
        </TextLink>
      );
    }
    default: {
      return undefined;
    }
  }
};

interface MealDealPageProps {
  id: string;
}

const MealDealPage = memo(({ id }: MealDealPageProps) => {
  const isPageLoading = useWtrSelector(getPageLoading);
  const isMealDealLoading = useWtrSelector(getMealDealLoading);
  const loading = isPageLoading || isMealDealLoading;

  const errorCode = useWtrSelector(getMealDealErrorCode);
  const notFound = useWtrSelector(getMealDealNotFound);
  const isUnrecoverableError = useWtrSelector(getMealDealUnrecoverableError);

  const startDate = useWtrSelector(getMealDealStartDate);
  const endDate = useWtrSelector(getMealDealEndDate);
  const groups = useWtrSelector(getMealDealProductsGrouped);
  const productsRecommended = useWtrSelector(getMealDealRecommendedProducts);
  const buildersLength = useWtrSelector(getMealDealBuildersLength);

  const slotDate = useWtrSelector(getCustomerSlotDate) as DateString | undefined;
  const editSlotUrl = useWtrSelector(getEditSlotUrl);
  const hasCMSHeaderContent = useWtrSelector(hasHeaderContent);
  const title = useWtrSelector(state => getMealDealTitle(state));

  const listDefinitions: ListAnalytics = useMemo(
    () => ({
      format: 'List',
      curation: 'Promotions',
      page: 'Meal Deal',
      title,
    }),
    [title],
  );

  const {
    alertCause,
    alertMessage,
    alertTitle,
    comingSoonMessage,
    isNowAfterEndDate,
    isNowBeforeStartDate,
    isSlotBooked,
    showAlert,
    showBuilder,
    showGrid,
  } = useMemo(
    () => getMealDealDateDrivenConfig({ endDate, errorCode, slotDate, startDate }),
    [endDate, errorCode, slotDate, startDate],
  );

  const getProducts = useCallback(
    (index: number) => [...groups.values()]?.[index] || EMPTY_PRODUCTS,
    [groups],
  );

  const [productCountAndTotal, setProductCountAndTotal] = useState<CountAndTotal | null>(null);
  const handleSnackbarClose = useCallback(() => setProductCountAndTotal(null), []);

  const metaData = useWtrSelector(getMealDealMetadata);

  if (notFound) {
    return <NotFound />;
  }

  const progressMessage = getMealDealProgressMessage(productCountAndTotal);
  const showCarousel = canShowRecommended(productsRecommended) && !isNowAfterEndDate;
  const hideMobileCarousel = buildersLength < 2;

  return (
    <>
      {/* @ts-expect-error untyped component */}
      <MetaHeader {...metaData} />
      <div className={styles.wrapper}>
        <article data-testid="meal-deal">
          <Breadcrumbs breadcrumbs={[]} />
          {!hasCMSHeaderContent && isNowAfterEndDate ? (
            <div className={classNames(styles.mealDealEnded, 'container-fluid')}>
              <Scene7Image
                imageName="meal-deal-ended-hero"
                altText=""
                preload
                lazyLoad={false}
                config={{ widths: [480, 768, 1260] }}
                width="1196"
              />
              <div className={styles.mealDealEndedInfo}>
                <PageTitle theme="underline" title="Meal deal has now ended" />
                <Typography styleAs="sectionHeadingLight">
                  The meal deal you are trying to view has now ended. Please see our other delicious{' '}
                  <TextLink component={Link} to={urls.mealDealDineIn}>
                    Dine In & Meal Deals
                  </TextLink>
                </Typography>
              </div>
            </div>
          ) : (
            <ContentLocation name="header" preloadImages />
          )}
        </article>
        <div className={classNames(styles.mealDealsPage)}>
          {showAlert && (
            <div
              className={classNames(
                styles.pageWideAlert,
                {
                  [styles.withGrid]: showGrid,
                },
                'container-fluid',
              )}
            >
              <Alert
                type={
                  alertCause === INVALID_SLOT_CAUSE || alertCause === INVALID_BRANCH_CAUSE
                    ? 'warning'
                    : 'info'
                }
                title={alertTitle}
                message={alertMessage}
                actions={getAlertAction(alertCause, editSlotUrl)}
              />
            </div>
          )}
          {isNowBeforeStartDate && !isSlotBooked && (
            <div className={styles.comingSoon}>
              <PageTitle theme="underline" title="Coming soon" />
              <Typography styleAs="sectionHeadingLight">
                <span>{comingSoonMessage}</span>
                <span>Subject to availability. Selected lines only</span>
              </Typography>
              <LinkAsButton label="Book a slot" component={Link} to={urls.serviceSelection} />
            </div>
          )}
          <div
            data-testid="meal-deal-wrapper"
            className={classNames(styles.mealDealsContainer, 'container-fluid', {
              [styles.error]: isUnrecoverableError,
            })}
          >
            {loading ? (
              <Skeleton
                withSpinner
                spinnerPosition="top"
                height="100vh"
                backgroundColor={colourScallopGrey}
                data-testid="products-loading"
              />
            ) : (
              <>
                <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
                  <div
                    data-testid="meal-deal-products-container"
                    className={classNames(
                      'col-sm-12of12',
                      'col-lg-8of12',
                      styles.productsContainer,
                    )}
                  >
                    {showGrid && (
                      <ProductGrid
                        isBuilderPage
                        groups={groups}
                        products={[...groups.values()].flat()}
                        loading={loading}
                        // @ts-expect-error untyped component
                        searchType={SEARCH_TYPE}
                        // @ts-expect-error untyped component
                        taxonomyLevel={TAXONOMY_LEVEL}
                        listDefinitions={listDefinitions}
                      />
                    )}
                  </div>
                </SSRMediaQuery>
                {showBuilder && (
                  <Builder
                    buildersLength={buildersLength}
                    GroupItemPlaceholder={GroupItemPlaceholder}
                    listDefinitions={listDefinitions}
                    location={LOCATION}
                    getProducts={getProducts}
                    searchType={SEARCH_TYPE}
                    setProductCountAndTotal={setProductCountAndTotal}
                    taxonomyLevel={TAXONOMY_LEVEL}
                    warning={BUILDERS_WARNING_MESSAGE}
                    multipleMessage={BUILDER_SUBSEQUENT_MESSAGE}
                    initialMessage={BUILDER_INITIAL_MESSAGE}
                    completeMessage={BUILDER_COMPLETE_MESSAGE}
                    variant="meal deal"
                  />
                )}
              </>
            )}
          </div>
          {showCarousel && (
            <div
              className={classNames('col-sm-12of12', styles.recommendations, {
                [styles.hideMobileCarousel]: hideMobileCarousel,
              })}
              data-testid="carousel-wrapper"
            >
              {/* @ts-expect-error untyped component */}
              <RecommendedCarousel
                list="mealdeal"
                products={productsRecommended}
                titleDesktop={RECOMMENDATIONS_TITLE}
                titleMobileAndTablet={RECOMMENDATIONS_TITLE}
                subTitle={RECOMMENDATIONS_SUB_TITLE}
                typeForAnalytics={GA_MEAL_DEAL_RECOMMENDED_CAROUSEL_TYPE}
              />
            </div>
          )}
        </div>
        <div className="container-fluid">
          <ExperienceFragment
            experienceFragmentKey={`meal-deals/seo-content/${id}/master`}
            locationName="seoContent"
            data-testid="meal-deal-seo-content"
          />
        </div>
      </div>
      {progressMessage && showBuilder && (
        <Snackbar
          data-testid="progressInfo"
          open
          placement="bottom"
          snapTo="window"
          type="success"
          onClose={handleSnackbarClose}
          // eslint-disable-next-line
          // @ts-ignore
          duration={3000}
        >
          {progressMessage}
        </Snackbar>
      )}
      <ContentLocation name="body" />
      <ContentLocation name="root" />
      {/**
       * TODO: Need to add the below line:
       *
       * ```tsx
       * <ContentLocation name="seoContent" />
       * ```
       *
       * Once AEM CI complete work to support AEM driven SEO content.
       *
       * REF: https://www.jlpit.com/jira/browse/WPIP-56196
       */}
    </>
  );
});

MealDealPage.displayName = 'MealDealPage';

export default MealDealPage;
